"use client"

import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"
import { cn } from "@/lib/utils"

// BOOTH's one overlay shape: a sheet that rises from the bottom edge, the way
// Apple Pay's checkout does.
//
// It replaces the centred dialog for everything a visitor is asked to commit
// to — inquiring on a role, inquiring on a booth, signing up — because those
// all end in a single tap on a primary button, and a sheet puts that button at
// the edge the thumb already rests on instead of halfway up the screen. One
// component so the three never drift apart again.
//
// Built on the Radix primitives rather than ui/dialog: DialogContent
// hard-codes the centring transform, and overriding it from the outside is how
// you end up with a sheet that is centred on some breakpoints.
//
// Two tones, one anchoring. `ink` (the default) is the black surface the
// marketing and visitor-facing sheets use. `surface` is the dashboard's light
// card — the owner tools (rental agreement, plan offer, a roster member) live
// on `--background`, and before this they each kept their own centred Dialog
// rather than go dark, which is how three of them ended up as cramped boxes
// floating in the middle of a phone screen.
//
// Pass `className` for per-sheet width or padding, never for the anchoring and
// never for the tone.

const BottomSheet = DialogPrimitive.Root
const BottomSheetTrigger = DialogPrimitive.Trigger
const BottomSheetClose = DialogPrimitive.Close
const BottomSheetTitle = DialogPrimitive.Title
const BottomSheetDescription = DialogPrimitive.Description

function BottomSheetContent({
  className,
  overlayClassName,
  children,
  showHandle = true,
  showCloseButton = true,
  closeDisabled = false,
  tone = "ink",
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Content> & {
  overlayClassName?: string
  showHandle?: boolean
  showCloseButton?: boolean
  /** Blocks the X while a submit is in flight; the overlay is disabled by
      the caller's own onOpenChange. */
  closeDisabled?: boolean
  /** `ink` is the black visitor-facing surface; `surface` is the dashboard's
      light card. Nothing else about the sheet changes between them. */
  tone?: "ink" | "surface" | "inverse"
}) {
  const isInk = tone === "ink"
  // inverse: the opposite of the page. Ink over a light page, white over a
  // dark one — which is bg-foreground text-background by definition, so one
  // set of classes serves both modes with no dark: variants. Children use
  // text-background for ink and bg-background/N for surfaces on it.
  const isInverse = tone === "inverse"
  return (
    <DialogPrimitive.Portal>
      <DialogPrimitive.Overlay
        className={cn(
          "fixed inset-0 z-50 bg-black/60 backdrop-blur-[2px]",
          "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0",
          overlayClassName,
        )}
        style={{
          // Start at the top of what is actually visible, not the top of the
          // layout viewport. On iOS Safari the keyboard scrolls the visual
          // viewport down inside the layout one, and a plain inset-0 overlay
          // stays behind with it — so the page showed through above the sheet
          // the moment a field was focused. --vv-top is 0 until that happens
          // (and everywhere ViewportInsets does not run), which is inset-0.
          top: "var(--vv-top, 0px)",
        }}
      />
      <DialogPrimitive.Content
        className={cn(
          // Anchored to the bottom edge at every width; from sm up it lifts
          // onto a floating card so it isn't stranded across a desktop screen.
          "fixed inset-x-0 bottom-0 z-50 mx-auto flex w-full max-w-[480px] flex-col",
          "overflow-y-auto overscroll-contain",
          "rounded-t-[28px] sm:bottom-6 sm:rounded-[28px]",
          isInk
            ? "bg-booth-ink text-white ring-1 ring-white/10"
            : isInverse
              ? "bg-foreground text-background ring-1 ring-background/10"
              : "bg-background text-foreground ring-1 ring-border",
          "shadow-[0_-8px_40px_rgba(0,0,0,0.45)]",
          "duration-300 data-[state=open]:animate-in data-[state=closed]:animate-out",
          "data-[state=open]:slide-in-from-bottom-full data-[state=closed]:slide-out-to-bottom-full",
          className,
        )}
        style={{
          paddingBottom: "env(safe-area-inset-bottom, 0px)",
          // Sit ON the keyboard rather than under it.
          //
          // --keyboard-h is published by NativeShell from Capacitor's
          // keyboardWillShow/Hide inside the app shell, and by ViewportInsets
          // from window.visualViewport in a browser (mobile Safari has no
          // keyboard event and does not resize the layout viewport, so the
          // sheet sat behind the keyboard there until the latter existed). It
          // is 0px wherever neither runs. Without it the sheet stays pinned
          // to the viewport's bottom edge while the keyboard covers the lower
          // half of it — which is how the Contact Shop sheet showed its
          // heading and hid the textarea you were typing into.
          bottom: "var(--keyboard-h, 0px)",
          // Inline because the cap has to subtract the keyboard, which no
          // single utility expresses. (The original note here said arbitrary
          // Tailwind values do not build in this project — that was true when
          // it was written and is not any more: `max-h-[92svh]` compiles to a
          // real rule today. The inline style stays for the calc, not for
          // that.) It has to shrink as the keyboard grows or a tall sheet
          // pushes its own top off the screen.
          maxHeight: "calc(92svh - var(--keyboard-h, 0px))",
        }}
        {...props}
      >
        {showHandle && (
          // The affordance that says this dismisses downward.
          <div className="flex justify-center pt-3 pb-1">
            <span className={cn("h-1 w-9 rounded-full", isInk ? "bg-white/20" : isInverse ? "bg-background/20" : "bg-foreground/15")} aria-hidden />
          </div>
        )}

        {showCloseButton && (
          <DialogPrimitive.Close
            disabled={closeDisabled}
            className={cn(
              "absolute right-4 top-4 z-10 flex h-8 w-8 items-center justify-center rounded-full transition-colors disabled:opacity-40",
              isInk
                ? "text-white/50 hover:bg-white/5 hover:text-white"
                : isInverse
                  ? "text-background/50 hover:bg-background/10 hover:text-background"
                  : "text-muted-foreground hover:bg-muted hover:text-foreground",
            )}
          >
            <X className="h-4 w-4" strokeWidth={1.75} />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        )}

        {children}
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  )
}

export {
  BottomSheet,
  BottomSheetClose,
  BottomSheetContent,
  BottomSheetDescription,
  BottomSheetTitle,
  BottomSheetTrigger,
}
